/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, FormGroup, ValidationErrors} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {AnimalTypeService} from '../../../_services/animal-type.service';
import {AnimalType} from '../../../_models/animal-type';
import {Helpers} from '../../../helpers';
import {EQ} from '../../../_enums/operators';
import {I18nService} from '../../../_services/i18n.service';
import {ID} from '../../../_enums/constants';
import {ENTER} from '@angular/cdk/keycodes';
import {isControlInvalid, isControlValid, TOUCHED_OR_SUBMITTED} from '../../../_enums/form-validation';

@Component({
  selector: 'app-select-animal-types',
  templateUrl: './select-animal-types.component.html',
  styleUrls: ['./select-animal-types.component.scss']
})
export class SelectAnimalTypesComponent implements OnInit, OnChanges {

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             BASE COMPONENT INPUTS                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Input() controlName?: string;
  @Input() disableEnterKeyPress?: boolean;
  @Input() formGroup?: FormGroup;
  @Input() index?: number;
  @Input() label?: string;
  @Input() readonly ?: boolean;
  @Input() showLabel?: boolean;
  @Input() showRequired?: boolean;
  @Input() showValidation?: string;
  @Input() submitted?: boolean;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             COMPONENT OUTPUTS                                                                                      ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Output() updated = new EventEmitter<AnimalType>();

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PROPERTIES                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public data: AnimalType[] = [];

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGICS METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(private dataService: AnimalTypeService, private i18n: I18nService, private toastr: ToastrService) {
    this.controlName = 'animalType';
    this.disableEnterKeyPress = false;
    this.formGroup = null;
    this.index = 0;
    this.label = null;
    this.readonly = false;
    this.showLabel = true;
    this.showRequired = true;
    this.showValidation = TOUCHED_OR_SUBMITTED;
    this.submitted = false;
  }

  ngOnInit() {
    // This is intentional
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadData();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORM CONTROLS GETTERS                                                                                 ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get control(): AbstractControl | null {
    return this.formGroup ? this.formGroup.get(this.controlName) : null;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VALUES GETTERS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public byId(id: number): AnimalType | null {
    const res = this.data.filter(v => v.id === id);
    return (0 < res.length) ? res[0] : null;
  }

  get controlId(): string {
    return `${this.controlName}_${this.index}_${ID}`;
  }

  get required(): boolean {
    return this.validators && this.validators.hasOwnProperty('required');
  }

  get showInvalid(): boolean {
    return isControlInvalid(this.control, this.showValidation, this.submitted);
  }

  get showValid(): boolean {
    return isControlValid(this.control, this.showValidation);
  }

  get validators(): ValidationErrors | null {
    return this.control && this.control.validator && this.control.validator(this.control);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              CHANGE LISTENERS                                                                                      ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public keypress(e): void {
    if (e.keyCode === ENTER && this.disableEnterKeyPress) {
      e.preventDefault();
    }
  }

  public onChange(e): void {
    if (e && e.target && e.target.value) {
      this.updated.emit(this.byId(Number(e.target.value)));
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              ASYNC INITIALISATION                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private checkOnlyOne() {
    if (1 === this.data.length) {
      if (this.control) {
        this.control.setValue(this.data[0].id);
      }

      this.updated.emit(this.data[0]);
    }
  }

  private loadData() {
    this.dataService.find({filter: ['enabled', EQ, true]}).subscribe((res: HttpResponse<AnimalType[]>) => {
      this.data = res.body;
      this.checkOnlyOne();
    }, (err: HttpErrorResponse) => this.handleError(err));
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PRIVATE METHODS                                                                                       ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private handleError(err: HttpErrorResponse): void {
    Helpers.setLoading(false);
    this.toastr.error(`${err.status} - ${this.i18n.get(err.status)}`);
  }

}
